import { ImagePaths } from '../../lib/imageUrls.js';

export const defenderForCloudAppsSessionPolicies = {
  id: 57,
  slug: 'defender-for-cloud-apps-session-policies',
  ogImage: '/og/courses/defender-for-cloud-apps-session-policies.jpg',
  courseCode: 'MDCA-SESS-001',
  featured: false,
  title: 'Microsoft Defender for Cloud Apps — Session Policy Masterclass',
  subtitle: 'Control real-time user sessions and govern SaaS data flows with Defender for Cloud Apps',

  valueProposition: 'Conditional Access gets users to the door — Defender for Cloud Apps session policies control what they do once inside. This masterclass trains engineers to implement session controls, access policies, and activity policies that protect sensitive data in SaaS applications, manage shadow IT risk, and govern OAuth application sprawl — with direct application to Copilot data governance, APRA CPS 234 cloud controls, and ASD Essential Eight application hardening.',

  duration: '16 hours (2 days intensive)',
  students: 'New',
  level: 'Advanced',
  image: ImagePaths.COURSE_4,
  shortDescription: 'MDCA-SESS-001 — Microsoft Defender for Cloud Apps session policy masterclass covering Conditional Access App Control, shadow IT, OAuth governance, and SaaS data protection for Australian organisations.',

  price: {
    aud: 999,
    currency: 'AUD',
    earlyBird: 799
  },

  stripeConfig: {
    priceId: 'price_1U3WMrKu1nyc322UZlCHv1gS',
    productId: 'prod_V3deKMXvCCiWqN'
  },

  snapshot: {
    whoItsFor: [
      'Identity and security engineers who completed the Conditional Access Workshop and need session control depth',
      'Compliance officers governing SaaS application usage and OAuth application sprawl',
      'Security architects designing Copilot data governance controls',
      'CASB practitioners transitioning to or consolidating on Microsoft Defender for Cloud Apps'
    ],
    accreditation: 'Aligned to ASD Essential Eight Maturity Level 2 (Application Control, User Application Hardening) and APRA CPS 234 cloud third-party risk management obligations'
  },

  logistics: {
    classSize: 'Maximum 10 participants — lab environment with Microsoft 365 E5 Security',
    requirements: [
      'Completion of Microsoft Entra Conditional Access Workshop (CA-WS-001) or equivalent CA production experience',
      'Microsoft 365 E5 or Microsoft 365 E5 Security licence (Defender for Cloud Apps is included)',
      'At least one third-party SaaS application in use for session policy lab exercises (Salesforce, Box, or similar)',
      'Application administrator or Security Administrator role in the Microsoft 365 tenant'
    ]
  },

  overview: 'Microsoft Defender for Cloud Apps (formerly MCAS) is the cloud access security broker (CASB) embedded in the Microsoft 365 E5 Security stack. This masterclass moves past the basics of app discovery and goes deep into the operational configuration that makes Defender for Cloud Apps a genuine data governance tool: Conditional Access App Control session policies that proxy SaaS sessions and enforce real-time controls, activity policies that alert on or block risky actions within sanctioned apps, access policies for unmanaged devices, and the OAuth app governance feature that manages consent and permissions for third-party applications. In the Australian context, session policies and OAuth governance are increasingly referenced in APRA CPS 234 cloud service risk assessments and in ACSC Essential Eight Application Control implementation guidance for cloud-delivered applications.',

  outcomes: [
    'Configure Conditional Access App Control to proxy SaaS sessions through Defender for Cloud Apps',
    'Implement session policies: block download, block cut/copy/paste, require step-up authentication on sensitive content',
    'Configure access policies for unmanaged and non-compliant devices with real-time session inspection',
    'Build activity policies that alert or block risky user actions within sanctioned SaaS applications',
    'Implement OAuth app governance: review, approve, and revoke third-party app permissions at scale',
    'Run a shadow IT discovery assessment and build an app sanction and unsanction workflow',
    'Configure Defender for Cloud Apps integration with Microsoft Purview sensitivity labels for file governance',
    'Produce an application governance evidence pack aligned to APRA CPS 234 and Essential Eight controls'
  ],

  audience: [
    'Identity and security engineers extending Conditional Access into real-time session control',
    'Security architects designing SaaS and Copilot data governance controls',
    'CASB practitioners consolidating onto Microsoft Defender for Cloud Apps from a third-party broker',
    'Compliance officers governing shadow IT and OAuth application sprawl',
    'Risk and assurance staff in APRA-regulated entities building CPS 234 third-party evidence',
    'SOC engineers triaging Defender for Cloud Apps alerts in Microsoft Sentinel'
  ],

  prerequisites: [
    'Completion of the Microsoft Entra Conditional Access Workshop (CA-WS-001) or equivalent production CA experience',
    'Microsoft 365 E5 or Microsoft 365 E5 Security licence — Defender for Cloud Apps is included',
    'Application Administrator or Security Administrator role in the Microsoft 365 tenant',
    'At least one third-party SaaS application in use for the session policy labs (Salesforce, Box or similar)',
    'Working understanding of SAML and OpenID Connect single sign-on flows'
  ],

  modules: [
    {
      number: 1,
      title: 'Shadow IT Discovery and App Governance Foundation',
      duration: '4 hours',
      topics: [
        'Defender for Cloud Apps architecture: discovery, proxy, and API-based connectors',
        'Shadow IT discovery: log collection from firewalls, proxies, and Defender for Endpoint integration',
        'App catalogue and risk scores: evaluating SaaS applications against GDPR, SOC 2, and APRA CPS 234 criteria',
        'App sanctioning and tagging: building an approved and unsanctioned app registry',
        'App connectors: configuring API access for Microsoft 365, Salesforce, Box, ServiceNow, and other sanctioned apps'
      ]
    },
    {
      number: 2,
      title: 'Conditional Access App Control — Session Policies',
      duration: '6 hours',
      topics: [
        'Conditional Access App Control architecture: how the reverse proxy works and browser session requirements',
        'Onboard applications to App Control: Microsoft 365, Salesforce, and custom SAML apps',
        'Session policies: monitor only, block download, block upload, and require step-up MFA on sensitive content',
        'File inspection: content inspection policies using sensitive information types and sensitivity labels',
        'Protect sensitive data on unmanaged devices: access policies scoping download controls by device compliance state'
      ]
    },
    {
      number: 3,
      title: 'Activity Policies and OAuth App Governance',
      duration: '4 hours',
      topics: [
        'Activity policies: detect and alert on risky user behaviours within sanctioned SaaS apps',
        'Anomaly detection policies: impossible travel, ransomware activity, and mass download alerts',
        'OAuth App Governance: review third-party app permissions, set publisher verification requirements, revoke overscoped consents',
        'OAuth policy automation: auto-revoke apps that exceed permissions or access non-sanctioned data sources',
        'APRA CPS 234 application risk evidence: shadow IT reports, OAuth governance posture, and app compliance scoring'
      ]
    },
    {
      number: 4,
      title: 'Advanced Integration and Governance Evidence Pack',
      duration: '2 hours',
      topics: [
        'Microsoft Purview sensitivity label integration: govern cloud files using labels from within Defender for Cloud Apps',
        'Microsoft Sentinel integration: stream Defender for Cloud Apps alerts for SOC investigation and SOAR response',
        'Copilot data governance: using session policies to prevent Copilot from accessing unsanctioned SaaS data sources',
        'Governance evidence pack: App Control policy exports, OAuth review reports, and shadow IT assessment for APRA CPS 234',
        'Capstone: design a session policy framework for an Australian financial services or healthcare organisation'
      ]
    }
  ],

  format: {
    type: 'Live online masterclass — 2 days intensive with a shared Microsoft 365 E5 Security lab',
    includes: [
      '16 hours of instructor-led masterclass sessions capped at 10 participants',
      'Step-by-step SAML application onboarding lab for Conditional Access App Control',
      'Session, access and activity policy templates you can adapt to your tenant',
      'OAuth app governance review workflow and auto-revoke policy configurations',
      'Shadow IT discovery assessment method with app sanction and unsanction registry template',
      'Capstone: a session policy framework for an Australian financial services or healthcare scenario',
      'APRA CPS 234 application governance evidence pack template',
      'Recorded sessions for 6 months and a certificate of completion'
    ]
  },

  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Two-day masterclass cohorts run every six weeks. Private cohorts can use your own sanctioned SaaS estate for the onboarding labs, subject to a data classification review'
  },

  certification: 'No standalone vendor exam covers Defender for Cloud Apps exclusively. This content contributes to the SC-200 Security Operations Analyst and SC-100 Cybersecurity Architect Expert certification domains. Participants receive an EDUC4TE certificate of completion.',

  faqs: [
    {
      question: 'Does this course require the SC-200 or SC-100 certification as a prerequisite?',
      answer: 'No — this is a practical operations course, not exam preparation. The prerequisite is the Microsoft Entra Conditional Access Workshop or equivalent production CA experience, because Conditional Access App Control is an extension of Conditional Access policy. SC-200 and SC-100 certification holders will find this course extends their exam knowledge into operational depth.'
    },
    {
      question: 'Does Defender for Cloud Apps session control work with all SaaS applications?',
      answer: 'Conditional Access App Control works with any SAML or OIDC application you can onboard as an enterprise application in Entra ID. Microsoft has built-in session controls for Microsoft 365 apps, Salesforce, ServiceNow, Box, and others. Custom SAML apps require additional onboarding configuration. The lab environment includes a step-by-step SAML app onboarding exercise.'
    },
    {
      question: 'How does this relate to Microsoft Copilot governance?',
      answer: 'Module 4 covers Copilot-specific session controls: preventing Copilot from querying unsanctioned data sources, blocking sensitive file downloads from Copilot-assisted sessions on unmanaged devices, and using sensitivity labels to limit what Copilot can surface in its responses. This is directly relevant for Australian organisations deploying Copilot and subject to Privacy Act 1988 or APRA CPS 234 obligations.'
    },
    {
      question: 'Is this course suitable for APRA-regulated entities?',
      answer: 'Yes. APRA CPS 234 requires regulated entities to assess the information security capabilities of their material service providers — including SaaS providers. Shadow IT discovery, OAuth app governance, and session policy reports from Defender for Cloud Apps provide directly usable evidence for CPS 234 third-party risk assessments. Module 3 and Module 4 build this evidence pack as a capstone deliverable.'
    }
  ],

  australianContext: true,
  complianceFrameworks: ['APRA CPS 234', 'ASD Essential Eight', 'ACSC ISM', 'Privacy Act 1988']
};
